import React, { useState } from 'react';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import classNames from 'classnames';
import { useModalStore } from '@/store/useModalStore.ts';
import ArrowDown from '@/assets/Icons/Arrow-Down-2.svg?react';
import styles from './MissingDataSection.module.scss';
import Pagination from '@/components/ui/Pagination/Pagination.tsx';
import DataComplianceOfficeData = App.Data.DataComplianceOfficeData;
import DataComplianceDepartmentData = App.Data.DataComplianceDepartmentData;
import DataComplianceNestedUserData = App.Data.DataComplianceNestedUserData;
import DataComplianceLegalEntityData = App.Data.DataComplianceLegalEntityData;
import DataComplianceUserData = App.Data.DataComplianceUserData;
import OpenInNewTabIcon from '@/assets/Icons/Open-In-New-Tab.svg?react';
import { Link } from 'react-router-dom';
import { externalLinks } from '@/constants/externalLinks.ts';
import { trackEvent, trackPostHogEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';

interface EntityRowProps {
	entity: DataComplianceLegalEntityData;
}

type GroupPreviewInitialTab = 'travel-arrangers' | 'trips';

interface GroupHierarchyFilters {
	legalEntityId: number;
	officeId?: number;
	departmentId?: number;
}

const TripCountLabel: React.FC<{ count: number }> = ({ count }) => (
	<>
		{count} {count === 1 ? 'trip' : 'trips'}
	</>
);

const TravelArrangerCountLabel: React.FC<{ count: number }> = ({ count }) => (
	<>
		{count} {count === 1 ? 'arranger' : 'arrangers'}
	</>
);

const openBookerTripsPreviewModal = (openModal: (modalName: string, data?: unknown) => void, userId: number) => {
	openModal('travelerDataCompliance', {
		userId,
	});
	trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
		deduplicationKey: `travel-arranger.${userId}.trips`,
		postHogEventName: 'data_gap_trips_clicked',
		postHogProperties: {
			category: 'data-gap',
			source: 'travel_arranger_row',
		},
	});
};

const openGroupTripsPreviewModal = (
	openModal: (modalName: string, data?: unknown) => void,
	title: string,
	bookers: DataComplianceNestedUserData[],
	totalMissingTrips: number,
	hierarchyFilters: GroupHierarchyFilters,
	subtitle?: string,
	initialTab: GroupPreviewInitialTab = 'travel-arrangers',
) => {
	openModal('travelerDataCompliance', {
		groupTitle: title,
		groupSubtitle: subtitle,
		totalMissingTrips,
		initialTab,
		...hierarchyFilters,
		bookers: bookers.map(booker => ({
			id: booker.id,
			name: booker.name,
			officeName: booker.officeName,
			departmentName: booker.departmentName,
			nonCompliantItinerariesCount: booker.nonCompliantItinerariesCount,
		})),
	});
	trackPostHogEvent('data_gap_open_traveler_itineraries_modal', {
		category: 'data-gap',
	});
	trackEvent(productAnalyticsEvents.climatePro.dataGap.tripDataCoverage.interact, {
		deduplicationKey: [
			'organization',
			hierarchyFilters.legalEntityId,
			hierarchyFilters.officeId ?? 'all-offices',
			hierarchyFilters.departmentId ?? 'all-departments',
			initialTab,
		].join('.'),
		postHogEventName:
			initialTab === 'travel-arrangers' ? 'data_gap_travel_arrangers_clicked' : 'data_gap_trips_clicked',
		postHogProperties: {
			category: 'data-gap',
			source: 'organization_table',
			group_type: subtitle,
		},
	});
};

const getOfficeBookers = (office: DataComplianceOfficeData): DataComplianceNestedUserData[] =>
	(office.children || []).flatMap(department => department.children || []);

const getEntityBookers = (entity: DataComplianceLegalEntityData): DataComplianceNestedUserData[] =>
	(entity.children || []).flatMap(office => getOfficeBookers(office));

const isUnassignedOrgUnit = (item: { id: number | null }) => item.id === null || item.id < 0;

const hasSingleUnassignedDepartment = (office: DataComplianceOfficeData) => {
	const departments = office.children || [];

	return departments.length === 1 && isUnassignedOrgUnit(departments[0]);
};

const hasSingleUnassignedOfficeAndDepartment = (entity: DataComplianceLegalEntityData) => {
	const offices = entity.children || [];
	const [office] = offices;

	return offices.length === 1 && isUnassignedOrgUnit(office) && hasSingleUnassignedDepartment(office);
};

interface MissingTripsButtonProps {
	count: number;
	onClick?: () => void;
}

export const MissingTripsButton: React.FC<MissingTripsButtonProps> = ({ count, onClick }) => (
	<button
		type="button"
		className={styles.missingTripsButton}
		onClick={event => {
			event.stopPropagation();
			onClick?.();
		}}
	>
		<TripCountLabel count={count} />
	</button>
);

interface TravelArrangersButtonProps {
	count: number;
	onClick?: () => void;
}

const TravelArrangersButton: React.FC<TravelArrangersButtonProps> = ({ count, onClick }) => (
	<button
		type="button"
		className={styles.travelArrangersButton}
		onClick={event => {
			event.stopPropagation();
			onClick?.();
		}}
	>
		<TravelArrangerCountLabel count={count} />
	</button>
);

const EntityRow: React.FC<EntityRowProps> = ({ entity }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { openModal } = useModalStore();
	const shouldOpenSingleUnassignedPath = hasSingleUnassignedOfficeAndDepartment(entity);

	const handleToggle = () => {
		if (entity.children && entity.children.length > 0) {
			setIsOpen(!isOpen);
		}
	};

	return (
		<>
			<tr
				onClick={handleToggle}
				className={classNames(styles.nestedRow, styles.fadeIn, {
					[styles.hasItems]: !!entity.children?.length,
				})}
			>
				<td colSpan={3}>
					<FlexBlock alignItems="center" columnGap="4px">
						{entity.children && entity.children?.length > 0 ? (
							<div className={classNames(styles.expandIcon, { [styles.expanded]: isOpen })}>
								<ArrowDown />
							</div>
						) : (
							<div className={styles.expandIconSpacer}></div>
						)}
						{entity.name}
					</FlexBlock>
				</td>
				<td className={styles.bookers}>
					<TravelArrangersButton
						count={entity.travellersCount}
						onClick={() =>
							openGroupTripsPreviewModal(
								openModal,
								entity.name,
								getEntityBookers(entity),
								entity.nonCompliantItinerariesCount,
								{ legalEntityId: entity.id },
								'Legal entity',
								'travel-arrangers',
							)
						}
					/>
				</td>
				<td className={classNames(styles.missingLogs, styles.entityMissingLogs)}>
					<MissingTripsButton
						count={entity.nonCompliantItinerariesCount}
						onClick={() =>
							openGroupTripsPreviewModal(
								openModal,
								entity.name,
								getEntityBookers(entity),
								entity.nonCompliantItinerariesCount,
								{ legalEntityId: entity.id },
								'Legal entity',
								'trips',
							)
						}
					/>
				</td>
			</tr>
			{isOpen &&
				entity.children?.map((office: DataComplianceOfficeData, index: number) => (
					<OfficeRow
						key={office.id || office.name}
						office={office}
						legalEntityId={entity.id}
						level={1}
						defaultOpen={shouldOpenSingleUnassignedPath && isUnassignedOrgUnit(office)}
						animationDelay={index * 50}
					/>
				))}
		</>
	);
};
export default EntityRow;

interface OfficeRowProps {
	office: DataComplianceOfficeData;
	legalEntityId: number;
	level: number;
	defaultOpen?: boolean;
	animationDelay?: number;
}

const OfficeRow: React.FC<OfficeRowProps> = ({ office, legalEntityId, level, defaultOpen = false }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const { openModal } = useModalStore();
	const shouldOpenSingleUnassignedDepartment = isUnassignedOrgUnit(office) && hasSingleUnassignedDepartment(office);

	const handleToggle = () => {
		if (office.children && office.children.length > 0) {
			setIsOpen(!isOpen);
		}
	};

	return (
		<>
			<tr
				className={classNames(styles.nestedRow, styles.officeRow, styles.fadeIn, {
					[styles.hasItems]: !!office.children?.length,
				})}
				onClick={handleToggle}
			>
				<td colSpan={3}>
					<div className={styles.nestedContent} style={{ paddingLeft: `${level * 15}px` }}>
						<FlexBlock alignItems="center" columnGap="4px">
							{office.children && office.children.length > 0 ? (
								<div className={classNames(styles.expandIcon, { [styles.expanded]: isOpen })}>
									<ArrowDown />
								</div>
							) : (
								<div className={styles.expandIconDisabled}>
									<ArrowDown />
								</div>
							)}
							{office.id === null ? 'Unspecified' : office.name}
							{(office.id === null || office.id < 0) && (
								<span className={styles.assignEntity}>
									<Link
										to={externalLinks.createEntityInSpotnana}
										target="_blank"
										onClick={e => e.stopPropagation()}
									>
										Assign <OpenInNewTabIcon />
									</Link>
								</span>
							)}
						</FlexBlock>
					</div>
				</td>
				<td className={styles.bookers}>
					<TravelArrangersButton
						count={office.travellersCount}
						onClick={() =>
							openGroupTripsPreviewModal(
								openModal,
								office.id === null ? 'Unspecified' : office.name,
								getOfficeBookers(office),
								office.nonCompliantItinerariesCount,
								{ legalEntityId, officeId: office.id },
								'Office',
								'travel-arrangers',
							)
						}
					/>
				</td>
				<td className={classNames(styles.missingLogs, styles.entityMissingLogs)}>
					<MissingTripsButton
						count={office.nonCompliantItinerariesCount}
						onClick={() =>
							openGroupTripsPreviewModal(
								openModal,
								office.id === null ? 'Unspecified' : office.name,
								getOfficeBookers(office),
								office.nonCompliantItinerariesCount,
								{ legalEntityId, officeId: office.id },
								'Office',
								'trips',
							)
						}
					/>
				</td>
			</tr>
			{isOpen &&
				office.children?.map((dept: DataComplianceDepartmentData, index: number) => (
					<DepartmentRow
						key={dept.id}
						dept={dept}
						legalEntityId={legalEntityId}
						officeId={office.id}
						level={level + 1}
						defaultOpen={shouldOpenSingleUnassignedDepartment && isUnassignedOrgUnit(dept)}
						animationDelay={index * 30}
					/>
				))}
		</>
	);
};

interface DepartmentRowProps {
	dept: DataComplianceDepartmentData;
	legalEntityId: number;
	officeId: number;
	level: number;
	defaultOpen?: boolean;
	animationDelay?: number;
}

const DepartmentRow: React.FC<DepartmentRowProps> = ({ dept, legalEntityId, officeId, level, defaultOpen = false }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);
	const [usersPagination, setUsersPagination] = useState({ currentPage: 1, perPage: 6 });
	const { openModal } = useModalStore();

	const handleToggle = () => {
		if (dept.children && dept.children.length > 0) {
			setIsOpen(!isOpen);
		}
	};

	const handleUsersPagination = (num: number) => {
		setUsersPagination({ currentPage: num, perPage: 6 });
	};

	const startIndex = (usersPagination.currentPage - 1) * usersPagination.perPage;
	const endIndex = startIndex + usersPagination.perPage;
	const paginatedUsers = dept.children?.slice(startIndex, endIndex) || [];

	return (
		<>
			<tr
				className={classNames(styles.nestedRow, styles.departmentRow, styles.fadeIn, {
					[styles.hasItems]: !!dept.children?.length,
				})}
				onClick={handleToggle}
			>
				<td colSpan={3}>
					<div className={styles.nestedContent} style={{ paddingLeft: `${level * 15}px` }}>
						<FlexBlock alignItems="center" columnGap="4px">
							{dept.children && dept.children.length > 0 ? (
								<div className={classNames(styles.expandIcon, { [styles.expanded]: isOpen })}>
									<ArrowDown />
								</div>
							) : (
								<div className={styles.expandIconDisabled}>
									<ArrowDown />
								</div>
							)}
							{dept.name}
						</FlexBlock>
						{(dept.id === null || dept.id < 0) && (
							<span className={styles.assignEntity}>
								<Link
									to={externalLinks.createEntityInSpotnana}
									target="_blank"
									onClick={e => e.stopPropagation()}
								>
									Assign <OpenInNewTabIcon />
								</Link>
							</span>
						)}
					</div>
				</td>
				<td className={styles.bookers}>
					<TravelArrangersButton
						count={dept.travellersCount || 0}
						onClick={() =>
							openGroupTripsPreviewModal(
								openModal,
								dept.name,
								dept.children || [],
								dept.nonCompliantItinerariesCount,
								{ legalEntityId, officeId, departmentId: dept.id },
								'Department',
								'travel-arrangers',
							)
						}
					/>
				</td>
				<td className={classNames(styles.missingLogs, styles.entityMissingLogs)}>
					<MissingTripsButton
						count={dept.nonCompliantItinerariesCount}
						onClick={() =>
							openGroupTripsPreviewModal(
								openModal,
								dept.name,
								dept.children || [],
								dept.nonCompliantItinerariesCount,
								{ legalEntityId, officeId, departmentId: dept.id },
								'Department',
								'trips',
							)
						}
					/>
				</td>
			</tr>
			{isOpen &&
				paginatedUsers.map((user: DataComplianceNestedUserData, index: number) => (
					<UserRow key={user.id} user={user} level={level + 1} animationDelay={index * 20} />
				))}
			{isOpen && dept.children && dept.children.length > usersPagination.perPage && (
				<tr className={styles.paginationRow}>
					<td colSpan={5}>
						<div>
							<Pagination
								records={dept.children.length}
								current={usersPagination.currentPage}
								onChange={handleUsersPagination}
								delta={2}
								fixed={1}
								limit={usersPagination.perPage}
							/>
						</div>
					</td>
				</tr>
			)}
		</>
	);
};

interface UserRowProps {
	user: DataComplianceNestedUserData | DataComplianceUserData;
	level: number;
	animationDelay?: number;
}

const UserRow: React.FC<UserRowProps> = ({ user, level }) => {
	const { openModal } = useModalStore();
	const openTripsPreview = () => openBookerTripsPreviewModal(openModal, user.id);

	return (
		<tr
			className={classNames(styles.nestedRow, styles.userRow, styles.fadeIn, styles.clickableRow)}
			onClick={openTripsPreview}
		>
			<td colSpan={4}>
				<div className={styles.nestedContent} style={{ paddingLeft: `${level * 15}px` }}>
					<FlexBlock alignItems="center" columnGap="4px" className={styles.userName}>
						<span className={styles.initials}>
							{user.name.trim().split(/\s+/)[0]?.charAt(0).toUpperCase()}
							{user.name.trim().split(/\s+/)[1]?.charAt(0).toUpperCase()}
						</span>
						{user.name}
					</FlexBlock>
				</div>
			</td>
			<td className={classNames(styles.missingLogs, styles.entityMissingLogs)}>
				<MissingTripsButton count={user.nonCompliantItinerariesCount} onClick={openTripsPreview} />
			</td>
		</tr>
	);
};
